import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, BookOpen, ChevronRight, Printer } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import LessonDocumentView from '@/features/connectlessen/LessonDocumentView';
import { printLesson } from '@/features/connectlessen/lessonPrint';
import { fetchLessonLibrary, fetchLibraryLesson } from './lessonLibraryApi';
import { LIBRARY_SECTIONS, errorMessage, libraryIsEmpty, lessonSubtitle } from './lessonLibraryModel';

const LessonRow = ({ lesson, current = false, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(lesson.id)}
    data-testid="library-lesson-row"
    className="group flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-teal-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
  >
    <span className="min-w-0">
      <span className="flex flex-wrap items-center gap-2">
        <span className="truncate font-medium text-slate-900">{lesson.title}</span>
        {current && <Badge>Eerstvolgende les</Badge>}
      </span>
      <span className="mt-0.5 block truncate text-sm text-slate-500">{lessonSubtitle(lesson) || 'Geen datum of serie'}</span>
      {lesson.theme && <span className="mt-0.5 block truncate text-sm italic text-slate-600">{lesson.theme}</span>}
    </span>
    <ChevronRight className="h-5 w-5 shrink-0 text-slate-300 transition group-hover:text-teal-600" />
  </button>
);

export const LessonDetail = ({ lesson, onBack, onPrint }) => (
  <div className="space-y-5" data-testid="library-lesson-detail">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="mr-1 h-4 w-4" />Terug naar lessen</Button>
      <Button variant="outline" size="sm" onClick={onPrint}><Printer className="mr-1 h-4 w-4" />Afdrukken</Button>
    </div>
    <Card className="border-0 shadow-sm">
      <CardContent className="p-5"><LessonDocumentView lesson={lesson} /></CardContent>
    </Card>
  </div>
);

// Alleen-lezen bibliotheek met gepubliceerde lessen. Bewust geen schrijf-, publiceer- of
// koppelacties: dat blijft bij de Connectlessen-module (auteursbeheer) en de bijeenkomst.
const LessonLibrary = ({ branding = { name: 'Zoea', logo_url: '' } }) => {
  const [library, setLibrary] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openLesson, setOpenLesson] = useState(null);
  const [opening, setOpening] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setLibrary(await fetchLessonLibrary());
      setError(null);
    } catch (loadError) {
      setError(errorMessage(loadError, 'Kon de lessenbibliotheek niet laden.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = async (lessonId) => {
    if (opening) return;
    setOpening(true);
    try {
      setOpenLesson(await fetchLibraryLesson(lessonId));
      setError(null);
    } catch (openError) {
      setError(errorMessage(openError, 'Kon de les niet openen.'));
      // De les kan inmiddels gedepubliceerd zijn: herlaad de lijst, maar behoud de melding.
      try { setLibrary(await fetchLessonLibrary()); } catch (reloadError) { /* melding blijft staan */ }
    } finally {
      setOpening(false);
    }
  };

  if (loading) return <div className="h-40 animate-pulse rounded-xl bg-slate-200" />;
  if (openLesson) {
    return <LessonDetail lesson={openLesson} onBack={() => setOpenLesson(null)} onPrint={() => printLesson(openLesson, null, branding)} />;
  }

  return (
    <div className="space-y-6" data-testid="lesson-library">
      {error && <p role="alert" className="rounded-md bg-amber-50 p-3 text-sm text-amber-900">{error}</p>}
      {library?.current && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Eerstvolgende les</h2>
          <LessonRow lesson={library.current} current onOpen={open} />
        </section>
      )}
      {library && LIBRARY_SECTIONS.map(({ key, title }) => (library[key]?.length ? (
        <section key={key} className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{title}</h2>
          {library[key].map((lesson) => <LessonRow key={lesson.id} lesson={lesson} onOpen={open} />)}
        </section>
      ) : null))}
      {!error && libraryIsEmpty(library) && (
        <div className="py-12 text-center">
          <BookOpen className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="font-medium text-slate-800">Nog geen gepubliceerde lessen</p>
          <p className="mt-1 text-sm text-slate-500">Zodra een connectles is gepubliceerd, verschijnt hij hier.</p>
        </div>
      )}
    </div>
  );
};

export default LessonLibrary;
